'use client';

import { useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { signIn } from 'next-auth/react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Spinner from '@/components/ui/spinner';
import { useAppLogging } from '@/utils/logger';

export default function SignInPage() {
    const logger = useAppLogging();
    const router = useRouter();

    const [loading, setLoading] = useState(false);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onSubmit = async (e: any) => {
        e.preventDefault();

        setLoading(true);

        try {
            const signInResponse = await signIn('credentials', {
                username: e.target.email.value,
                password: e.target.password.value,
                redirect: false,
            });

            if (signInResponse?.error) {
                throw new Error(signInResponse.error);
            }

            if (signInResponse?.ok) {
                router.push('/');
            }
        } catch (error) {
            logger.error('[frontend][client][SignIn] Failed to sign in', {
                error: error instanceof Error ? error.message : String(error),
            });
            setLoading(false);
        }
    };

    return (
        <div className="flex h-screen items-center justify-center">
            <div className="bg-card border-muted-foreground/20 w-full max-w-md rounded-md border p-8">
                {process.env.NEXT_PUBLIC_CREDENTIALS_AUTH ? (
                    <>
                        <h1 className="mb-6 text-2xl font-bold">Inloggen</h1>
                        <form onSubmit={onSubmit}>
                            <div className="mb-4">
                                <Label className="mb-2" htmlFor="email">
                                    Email
                                </Label>
                                <Input
                                    disabled={loading}
                                    type="email"
                                    id="email"
                                    required
                                />
                            </div>

                            <div className="mb-6">
                                <Label className="mb-2" htmlFor="password">
                                    Wachtwoord
                                </Label>
                                <Input
                                    disabled={loading}
                                    type="password"
                                    id="password"
                                    required
                                />
                            </div>

                            <Button
                                disabled={loading}
                                type="submit"
                                variant="outline"
                                className="w-full"
                            >
                                {loading ? <Spinner size={4} /> : 'Inloggen'}
                            </Button>
                        </form>
                    </>
                ) : (
                    <>
                        <Image
                            src={`/${process.env.NEXT_PUBLIC_LOGO_DARK_FILE ?? 'datadialogue_wordmark_dark.svg'}`}
                            alt="DataDialogue logo"
                            width={150}
                            height={150}
                            className="mb-4 object-contain dark:hidden"
                        />
                        <Image
                            src={`/${process.env.NEXT_PUBLIC_LOGO_FILE ?? 'datadialogue_wordmark.svg'}`}
                            alt="DataDialogue logo"
                            width={150}
                            height={150}
                            className="mb-4 hidden object-contain dark:block"
                        />
                        <p className="mb-4">
                            Gebruik uw Microsoft-account om in te loggen.
                        </p>
                        <Button
                            variant="outline"
                            className="flex h-auto items-start justify-start px-4 py-2"
                            onClick={async () => {
                                await signIn('azure-ad');
                                router.push('/');
                            }}
                        >
                            <Image
                                src="/ms-logo.png"
                                alt="Microsoft logo"
                                width={15}
                                height={15}
                                className="mr-2"
                            />
                            Inloggen
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}
